import { Box, Divider, Stack, Typography, useTheme } from "@mui/material";
import { useEffect, useMemo, useState } from "react";
import { QuestionType } from "../../../../constants";
import { GetSurveyCoherenceReportResultDto } from "../../../../http";
import { QuestionFilter } from "./QuestionFilter";
import { TextResponse } from "./TextResponse";
import { TextResponseSkeleton } from "./TextResponseSkeleton";
import { useBreakpoints } from "src/hooks";

interface IProps {
  isLoading: boolean;
  report: GetSurveyCoherenceReportResultDto | undefined;
}

const TextResponses = (props: IProps) => {
  const [selectedQuestionGuids, setSelectedQuestionGuids] = useState<string[]>([]);
  const { isMdUp } = useBreakpoints();

  useEffect(() => {
    // Select all questions by default.
    setSelectedQuestionGuids(
      Object.entries(props.report?.questions ?? {})
        .filter(([, question]) => question.questionTypeId === QuestionType.Text)
        .map(([questionGuid]) => questionGuid),
    );
  }, [props.report]);

  const theme = useTheme();

  const groupedResponses = useMemo(() => {
    if (!props.report) {
      return [];
    }
    const filtered = props.report.textResponses.filter((x) => selectedQuestionGuids.includes(x.questionGuid));

    const groups = filtered.reduce(
      (acc, response) => {
        if (!acc[response.questionGuid]) {
          acc[response.questionGuid] = [];
        }
        acc[response.questionGuid].push(response);
        return acc;
      },
      {} as Record<string, typeof filtered>,
    );

    return Object.keys(groups)
      .map((questionGuid) => ({
        questionGuid,
        question: props.report!.questions[questionGuid],
        responses: groups[questionGuid].sort(
          (a, b) => a.teamName.localeCompare(b.teamName) || a.dateTimeUtc.localeCompare(b.dateTimeUtc),
        ),
      }))
      .sort((a, b) => a.question.name.localeCompare(b.question.name));
  }, [props.report, selectedQuestionGuids]);

  return (
    <Stack sx={{ height: "100%", overflow: "hidden" }}>
      {isMdUp && (
        <>
          <QuestionFilter
            questions={Object.entries(props.report?.questions ?? {})
              .filter(([, question]) => question.questionTypeId === QuestionType.Text)
              .map(([questionGuid, question]) => ({
                questionGuid: questionGuid,
                questionName: question.name,
                questionTags: question.tags,
              }))}
            selectedQuestionGuids={selectedQuestionGuids}
            onChange={(values) => setSelectedQuestionGuids(values)}
            disabled={props.isLoading || props.report === undefined}
          />
          <Divider />
        </>
      )}
      <Stack divider={<Divider sx={{ color: "#c9c9c9" }} />} sx={{ m: 0, overflowY: "auto", flex: 1, minHeight: 0 }}>
        {props.isLoading ? (
          <TextResponseSkeleton sx={{ px: { xs: 0.5, md: 1 }, py: 0.5 }} />
        ) : groupedResponses.length > 0 ? (
          groupedResponses.map((group) => (
            <Box key={group.questionGuid}>
              <Stack position="sticky" sx={{ top: 0, zIndex: 1, bgcolor: theme.palette.common.white }}>
                <Stack sx={{ px: { xs: 0.5, md: 1 }, py: { xs: 0.5, md: 0.75 } }}>
                  <Typography variant="body2" color={theme.palette.common.black}>
                    {group.question.name}
                  </Typography>
                  <Typography variant="caption">{group.question.description}</Typography>
                </Stack>
                <Divider sx={{ color: theme.palette.grey[500] }} />
              </Stack>

              <Stack
                divider={<Divider variant="dashed" sx={{ color: "#c9c9c9" }} />}
                spacing={{ xs: 0.5, md: 1 }}
                p={{ xs: 0.5, md: 1 }}
              >
                {group.responses.map((x) => (
                  <TextResponse
                    key={x.answerGuid}
                    responseAnswer={{
                      questionText: group.question.name,
                      teamName: x.teamName,
                      submittedOnDateTimeUtc: x.dateTimeUtc,
                      answerGuid: x.answerGuid,
                      textAnswer: x.response,
                      createdItemCount: x.createdItemCount,
                    }}
                  />
                ))}
              </Stack>
            </Box>
          ))
        ) : (
          <Typography sx={{ px: { xs: 0.5, md: 1 }, py: 0.5 }}>
            No responses for selected questions and entities.
          </Typography>
        )}
      </Stack>
    </Stack>
  );
};

export { TextResponses };
